import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { UserContext, IMPERSONATION_START_IDX_KEY } from "./UserContext";
import { storage } from "../helpers/utils";

/**
 * Where this entry sits in the browser history.
 *
 * React Router v6 keeps a counter at history.state.idx that goes up with every
 * navigation and back down with Back. Null when unavailable, which turns the
 * automatic return off rather than guessing.
 */
const currentHistoryIdx = () => {
  const idx = window.history.state?.idx;
  return typeof idx === "number" ? idx : null;
};

/**
 * Guards a page that only some sessions may see.
 *
 * BACK PAST THE FIRST IMPERSONATED PAGE RETURNS TO ADMIN. becomeUser records
 * the history position of the admin page it started from. Landing on that
 * position or earlier while impersonating means Back was pressed past the first
 * impersonated page, so the admin session is restored automatically and the
 * page renders as it was. Back between the impersonated user's own pages never
 * reaches this guard, so that flow is untouched.
 *
 * ONLY ON A FRESH MOUNT. The admin page you become a user FROM is the same
 * history entry Back later returns to, so position alone cannot tell them
 * apart — and becomeUser switches the user while that page is still on screen.
 * The difference is that Back mounts the page anew, already impersonating,
 * where becoming a user happens on a page that mounted as admin. `armed`
 * captures that at mount, and disarms once the admin session is back so a
 * second becomeUser from the same page is not mistaken for Back either.
 *
 * Reaching an admin page any OTHER way while impersonating gets a prompt
 * instead, since there the intent is not clear.
 *
 * The page never mounts under the wrong session, so no request goes out with
 * the impersonated token and there is no 403.
 *
 * `allow` receives the UserContext value and returns whether this session may
 * see the page, e.g. allow={(c) => c.showUsers}.
 */
export default function RequireRole({ allow, children }) {
  const ctx = useContext(UserContext);
  const { user, isImpersonating, exitUser, resolvePostLoginRoute } = ctx;
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const exiting = useRef(false);
  const armed = useRef(isImpersonating);

  // A token but no user yet means the profile is still loading after a hard
  // refresh. Deciding now would flash the prompt at an admin who belongs here.
  const loading = !user && Boolean(storage.read("token"));
  const allowed = Boolean(user) && allow(ctx);

  // Read raw first: Number(null) is 0, which would look like a recorded start.
  const rawStart = storage.read(IMPERSONATION_START_IDX_KEY);
  const startIdx = rawStart == null || rawStart === "" ? NaN : Number(rawStart);
  const idx = currentHistoryIdx();
  const backPastStart =
    armed.current &&
    isImpersonating &&
    Number.isFinite(startIdx) &&
    idx !== null &&
    idx <= startIdx;

  useEffect(() => {
    if (allowed) armed.current = false;
  }, [allowed]);

  useEffect(() => {
    if (loading || allowed || !backPastStart || exiting.current) return;
    exiting.current = true;
    exitUser().finally(() => {
      exiting.current = false;
    });
  }, [loading, allowed, backPastStart, exitUser, location.key]);

  if (loading || (!allowed && backPastStart)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6C63FF" }} />
      </Box>
    );
  }

  if (allowed) return children;

  const name =
    `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "this user";

  const returnToAdmin = async () => {
    setBusy(true);
    // Stays on this route: once the admin session is back, allow() passes and
    // the page mounts with the admin token.
    await exitUser();
    setBusy(false);
  };

  const stayAsUser = async () => {
    setBusy(true);
    const route = await resolvePostLoginRoute();
    setBusy(false);
    navigate(route, { replace: true });
  };

  return (
    <Box sx={{ maxWidth: "480px", mx: "auto", mt: 8, px: 2, textAlign: "center" }}>
      {isImpersonating ? (
        <>
          <Typography sx={{ color: "#465578", fontSize: "18px", fontWeight: 600, mb: 1 }}>
            You're viewing as {name}
          </Typography>
          <Typography sx={{ color: "#6579A8", mb: 3 }}>
            This page is for admins. Return to your admin account to see it, or
            stay on {name}'s screens.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              disabled={busy}
              onClick={returnToAdmin}
              sx={{
                background: "#6C63FF",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { background: "#5a52e0" },
              }}
            >
              Return to admin
            </Button>
            <Button
              disabled={busy}
              onClick={stayAsUser}
              sx={{ color: "#6C63FF", textTransform: "none", fontWeight: 600 }}
            >
              Stay as {name}
            </Button>
          </Box>
        </>
      ) : (
        <Typography sx={{ color: "#6579A8" }}>
          You don't have access to this page.
        </Typography>
      )}
    </Box>
  );
}
